import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { formatBytes, formatDuration, getAudioDuration, validateAudioFile } from '../lib/audio';
import { CHUNK_THRESHOLD_BYTES } from '../lib/ffmpeg';
import { TRANSCRIPTION_MODELS, SUMMARY_MODELS } from '../lib/models';
import { ModelSelector } from './ModelSelector';
import type { FileInfo } from '../types';

const MAX_SUMMARY_CONTEXT_LENGTH = 6000;

interface Props {
  onFileSelected: (info: FileInfo) => void;
  onProcess: () => void;
  current: FileInfo | null;
  disabled: boolean;
  isProcessing: boolean;
  transcriptionModel: string;
  summaryModel: string;
  summaryContext: string;
  onTranscriptionModelChange: (id: string) => void;
  onSummaryModelChange: (id: string) => void;
  onSummaryContextChange: (value: string) => void;
}

export function FileUploader({
  onFileSelected,
  onProcess,
  current,
  disabled,
  isProcessing,
  transcriptionModel,
  summaryModel,
  summaryContext,
  onTranscriptionModelChange,
  onSummaryModelChange,
  onSummaryContextChange,
}: Props) {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      const v = validateAudioFile(file);
      if (!v.ok) {
        setValidationError(v.reason);
        return;
      }
      setValidationError(null);
      const duration = await getAudioDuration(file);
      onFileSelected({ file, durationSec: duration });
    },
    [onFileSelected]
  );

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragActive(true);
  };

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onClickZone = () => {
    if (!disabled) inputRef.current?.click();
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  const willChunk = current && current.file.size > CHUNK_THRESHOLD_BYTES;

  return (
    <section className="reveal-up" style={{ animationDelay: '120ms' }}>
      <p className="label-mono mb-4">
        <span className="text-[var(--color-amber-mid)]">[02]</span> · stdin
        <span className="text-[var(--color-asphalt-300)]"> // drop file or click_to_select</span>
      </p>

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={onClickZone}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClickZone();
          }
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        aria-label="Soltar archivo de audio o hacer clic para seleccionar"
        aria-disabled={disabled}
        className={[
          'relative cursor-pointer transition-all duration-300 term-box',
          'px-8 py-14 sm:py-20',
          'dashed-tension',
          dragActive ? 'dashed-tension-active scale-[1.005]' : '',
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[var(--color-amber-mid)]',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".m4a,.mp3,.wav,.flac,.aac,.ogg,.aiff,audio/*"
          onChange={onInputChange}
          className="hidden"
          disabled={disabled}
        />

        {!current ? (
          <div className="flex flex-col items-center text-center gap-4 relative z-10">
            <pre
              className="font-mono text-[10px] sm:text-xs leading-[1.15] text-[var(--color-asphalt-400)] select-none"
              aria-hidden
            >{`┌────────────────────┐
│    ▼ DROP_AUDIO    │
│  [ .m4a  /  .mp3 ] │
└────────────────────┘`}</pre>
            <div className="space-y-1">
              <p className="heading-display text-3xl sm:text-4xl">
                $ <em>upload</em> ./meeting<span className="term-caret" aria-hidden />
              </p>
              <p className="font-mono text-sm text-[var(--color-asphalt-500)] tracking-wide">
                arrastra el archivo · click para abrir el filesystem
              </p>
            </div>
            <p className="label-mono mt-2">
              <span className="text-[var(--color-amber-mid)]">accepts:</span> m4a · mp3 · wav · flac · aac · ogg
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center gap-3 relative z-10">
            <p className="label-mono">
              <span className="text-[var(--color-amber-mid)]">[loaded]</span> file_descriptor=0
            </p>
            <p className="heading-display text-2xl sm:text-3xl break-anywhere max-w-2xl">
              {current.file.name}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 font-mono text-xs text-[var(--color-asphalt-500)] nums-tabular">
              <span>{formatBytes(current.file.size)}</span>
              {current.durationSec !== null && (
                <>
                  <span className="text-[var(--color-asphalt-300)]">│</span>
                  <span>{formatDuration(current.durationSec)}</span>
                </>
              )}
              <span className="text-[var(--color-asphalt-300)]">│</span>
              <span>{current.file.type || 'audio/*'}</span>
            </div>
            {willChunk && (
              <p className="font-mono text-[10px] text-[var(--color-amber-deep)] uppercase tracking-[0.14em] mt-2 glow-cyan">
                ◆ ffmpeg.wasm split() activado · supera 24MB
              </p>
            )}
            {!isProcessing && (
              <p className="font-mono text-xs text-[var(--color-asphalt-400)] mt-2">
                <span className="text-[var(--color-asphalt-300)]">// </span>click para reemplazar el descriptor
              </p>
            )}
          </div>
        )}
      </div>

      {validationError && (
        <p className="mt-3 font-mono text-xs text-[var(--color-burgundy)]" role="alert">
          <span className="glow-cyan" style={{ textShadow: '0 0 6px rgba(255,46,77,0.6)' }}>[err]</span> {validationError}
        </p>
      )}

      <div className="mt-8 space-y-6 term-box p-5 sm:p-6 relative z-10">
        <p className="label-mono">
          <span className="text-[var(--color-amber-mid)]">[config]</span> ./pipeline.toml
          <span className="text-[var(--color-asphalt-300)]"> // selecciona los modelos</span>
        </p>
        <ModelSelector
          label="set transcribe.model"
          prompt="audio → texto"
          options={TRANSCRIPTION_MODELS}
          value={transcriptionModel}
          onChange={onTranscriptionModelChange}
          disabled={disabled}
        />
        <ModelSelector
          label="set summary.model"
          prompt="texto → resumen estructurado"
          options={SUMMARY_MODELS}
          value={summaryModel}
          onChange={onSummaryModelChange}
          disabled={disabled}
        />
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <div>
              <label
                htmlFor="summary-context"
                className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-asphalt)]"
              >
                set summary.context
              </label>
              <p className="font-mono text-[10px] text-[var(--color-asphalt-400)] mt-1">
                participantes, objetivo, agenda, notas previas o nombres propios
              </p>
            </div>
            <span className="font-mono text-[10px] text-[var(--color-asphalt-400)] nums-tabular">
              {summaryContext.length}/{MAX_SUMMARY_CONTEXT_LENGTH}
            </span>
          </div>
          <textarea
            id="summary-context"
            value={summaryContext}
            onChange={(e) => onSummaryContextChange(e.target.value)}
            disabled={disabled}
            maxLength={MAX_SUMMARY_CONTEXT_LENGTH}
            rows={6}
            placeholder="Ej: Participaron Ana, Carlos y el equipo de Producto. La reunión fue sobre el roadmap Q2; revisar riesgos de implementación, decisiones pendientes y próximos pasos. Notas: priorizar migración de pagos y dejar explícitos los due dates."
            className="term-textarea w-full resize-y min-h-32 px-4 py-3 font-mono text-xs leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {current && !isProcessing && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onProcess}
            disabled={disabled}
            className="term-btn term-btn-primary group"
          >
            <span>$ exec ./process</span>
            <span className="text-[var(--color-ink)] transition-transform group-hover:translate-x-1">
              →
            </span>
          </button>
        </div>
      )}
    </section>
  );
}
